import { motion } from "motion/react"
import { Stars02 } from "@untitledui/icons"

// Quick Estimate 3 — illustrated empty state for the photo step.
//
// Sells the value-prop in seconds: a phone framing a car with an AI
// tag bubble floating to the side. Keeps the visual quiet (zinc
// tones for the silhouette, indigo for the AI element) so it
// doesn't compete with the shutter button above.
//
// Single SVG; no external assets. Designed at 240×140 viewbox so
// it scales cleanly on any width.

export function EmptyStateIllustration() {
  return (
    <div className="flex flex-col items-center text-center gap-3 py-3">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative w-[200px] h-[120px]"
      >
        <svg
          viewBox="0 0 240 140"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full"
          aria-hidden
        >
          {/* Background glow */}
          <ellipse
            cx="120"
            cy="120"
            rx="90"
            ry="14"
            fill="url(#shadow)"
            opacity="0.5"
          />
          {/* Phone */}
          <rect
            x="42"
            y="22"
            width="106"
            height="100"
            rx="14"
            stroke="rgb(161 161 170)"
            strokeWidth="2"
            fill="white"
          />
          <rect
            x="50"
            y="32"
            width="90"
            height="72"
            rx="6"
            fill="rgb(244 244 245)"
          />
          {/* Camera viewfinder corners */}
          <CornerBracket x={56} y={38} flip="tl" />
          <CornerBracket x={134} y={38} flip="tr" />
          <CornerBracket x={56} y={98} flip="bl" />
          <CornerBracket x={134} y={98} flip="br" />
          {/* Car silhouette inside the viewfinder */}
          <path
            d="M65 76 q3 -10 14 -12 l8 -7 q4 -3 11 -3 h17 q7 0 11 3 l8 7 q11 2 14 12 v6 h-7 a4 4 0 1 1 -8 0 h-43 a4 4 0 1 1 -8 0 h-7 z"
            fill="rgb(82 82 91)"
          />
          {/* Window */}
          <path
            d="M85 65 l5 -5 q3 -2 7 -2 h17 q4 0 7 2 l5 5 v6 h-41 z"
            fill="rgb(212 212 216)"
          />
          {/* Wheels */}
          <circle cx="80" cy="84" r="3" fill="rgb(24 24 27)" />
          <circle cx="123" cy="84" r="3" fill="rgb(24 24 27)" />
          {/* Phone home indicator */}
          <rect
            x="86"
            y="111"
            width="20"
            height="3"
            rx="1.5"
            fill="rgb(212 212 216)"
          />
          {/* AI bubble (right of phone) */}
          <g transform="translate(165 56)">
            <path
              d="M-4 18 l4 -6 h32 a8 8 0 0 0 8 -8 v-12 a8 8 0 0 0 -8 -8 h-36 a8 8 0 0 0 -8 8 v12 a8 8 0 0 0 8 8"
              fill="rgb(238 242 255)"
              stroke="rgb(199 210 254)"
              strokeWidth="1"
            />
            <circle cx="-2" cy="-4" r="1.3" fill="rgb(99 102 241)" />
            <text
              x="6"
              y="0"
              fontSize="9"
              fontWeight="700"
              fill="rgb(67 56 202)"
              fontFamily="ui-sans-serif, system-ui"
            >
              FRONT
            </text>
          </g>
          {/* Sparkle dots (decorative) */}
          <motion.g
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <circle cx="200" cy="32" r="2" fill="rgb(99 102 241)" />
            <circle cx="208" cy="44" r="1.4" fill="rgb(165 180 252)" />
            <circle cx="32" cy="48" r="1.4" fill="rgb(165 180 252)" />
            <circle cx="22" cy="74" r="2" fill="rgb(99 102 241)" />
          </motion.g>
          <defs>
            <linearGradient id="shadow" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="rgb(0 0 0)" stopOpacity="0.08" />
              <stop offset="1" stopColor="rgb(0 0 0)" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </motion.div>
      <div className="space-y-1.5">
        <p className="text-[13.5px] font-semibold text-zinc-700 leading-tight">
          Snap any angle — the AI tags it.
        </p>
        <p className="inline-flex items-center gap-1 text-[11.5px] text-indigo-600 font-medium">
          <Stars02 className="size-3" aria-hidden />
          No sides to remember, no order to follow
        </p>
      </div>
    </div>
  )
}

// Corner bracket marks for the viewfinder. flip controls which two
// edges the bracket draws — tl=top-left, tr=top-right, etc.
function CornerBracket({
  x,
  y,
  flip,
}: {
  x: number
  y: number
  flip: "tl" | "tr" | "bl" | "br"
}) {
  // Two short strokes meeting at (x,y). For each corner, both
  // strokes point inward toward the rectangle's center.
  const len = 6
  const stroke = "rgb(99 102 241)"
  const sw = 1.6
  const horiz = (
    <line
      x1={x}
      y1={y}
      x2={
        flip === "tl" || flip === "bl" ? x + len : x - len
      }
      y2={y}
      stroke={stroke}
      strokeWidth={sw}
      strokeLinecap="round"
    />
  )
  const vert = (
    <line
      x1={x}
      y1={y}
      x2={x}
      y2={
        flip === "tl" || flip === "tr" ? y + len : y - len
      }
      stroke={stroke}
      strokeWidth={sw}
      strokeLinecap="round"
    />
  )
  return (
    <g>
      {horiz}
      {vert}
    </g>
  )
}
